import type { SpreadsheetSelectionRange } from './addressing'
import { normalizeSelectionRange } from './addressing'

export type SelectionSummary = {
  average: number | null
  count: number
  numericalCount: number
  sum: number
  truncated: boolean
}

export const SELECTION_SUMMARY_CELL_LIMIT = 10_000

function parseSelectionNumericValue(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const trimmed = String(raw ?? '').trim()
  if (!trimmed) return null
  const isParenthesizedNegative = /^\(.+\)$/.test(trimmed)
  const withoutParens = isParenthesizedNegative ? trimmed.slice(1, -1).trim() : trimmed
  const isPercent = /%$/.test(withoutParens)
  const normalized = withoutParens
    .replace(/[%,$£€¥\s]/g, '')
    .replace(/[−–—]/g, '-')
  if (!normalized || normalized === '-' || normalized === '.') return null
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) return null
  const signed = isParenthesizedNegative ? -numeric : numeric
  return isPercent ? signed / 100 : signed
}

export function summarizeSelectionValues(
  selection: SpreadsheetSelectionRange | null | undefined,
  readValue: (col: number, row: number) => unknown,
  limit = SELECTION_SUMMARY_CELL_LIMIT,
): SelectionSummary {
  if (!selection) {
    return { average: null, count: 0, numericalCount: 0, sum: 0, truncated: false }
  }
  const range = normalizeSelectionRange(selection)
  const maxCells = Math.max(1, Math.floor(Number(limit) || SELECTION_SUMMARY_CELL_LIMIT))
  let visited = 0
  let count = 0
  let numericalCount = 0
  let sum = 0
  let truncated = false

  for (let row = range.row; row <= range.endRow; row++) {
    for (let col = range.col; col <= range.endCol; col++) {
      if (visited >= maxCells) {
        truncated = true
        return {
          average: numericalCount > 0 ? sum / numericalCount : null,
          count,
          numericalCount,
          sum,
          truncated,
        }
      }
      visited += 1
      const raw = readValue(col, row)
      if (raw == null || raw === '') continue
      count += 1
      const numeric = parseSelectionNumericValue(raw)
      if (numeric != null) {
        numericalCount += 1
        sum += numeric
      }
    }
  }

  return {
    average: numericalCount > 0 ? sum / numericalCount : null,
    count,
    numericalCount,
    sum,
    truncated,
  }
}

export function formatSelectionSummaryNumber(value: number): string {
  if (!Number.isFinite(value)) return ''
  const abs = Math.abs(value)
  const maximumFractionDigits = abs >= 100 ? 2 : abs >= 1 ? 4 : 6
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value)
}
